import { useMemo } from "react";

import { useParams } from "react-router-dom";
import { z } from "zod";

import {
  type ProjectSharingBaseRole,
  ProjectSharingBaseRoles,
  useCreateProjectInvitation,
  useProjectSharing,
} from "../../../../../hooks/useProjectSharing";
import useTracker from "../../../../../utils/useTracker";
import { EmailInvitationForm } from "./EmailInvitationForm";

export const EmailInvitationFormProject = () => {
  const tracker = useTracker();
  const { projectId } = useParams();
  const {
    mutateAsync: mutateCreateProjectInvitation,
    isPending: isMutateCreateViewLinkInvitationPending,
    isError: isMutateCreateViewLinkInvitationError,
  } = useCreateProjectInvitation({ onSuccess: () => {} });

  const baseRoleSchema = z.enum([...Object.values(ProjectSharingBaseRoles)] as [
    ProjectSharingBaseRole,
    ...ProjectSharingBaseRole[],
  ]);

  const projectInvitationSchema = z.object({
    emails: z.array(z.string()),
    baseRole: baseRoleSchema,
  });

  const { data: projectViewLinkSharingData, isError } = useProjectSharing();
  const currentEmails = useMemo(() => {
    return [
      ...(projectViewLinkSharingData?.users.map((user) => user.email) ?? []),
      ...(projectViewLinkSharingData?.space.spaceUsers?.map((spaceUser) => spaceUser.email) ?? []),
    ];
  }, [projectViewLinkSharingData]);

  return (
    <EmailInvitationForm
      onSubmit={(data) => {
        return mutateCreateProjectInvitation(data);
      }}
      schema={projectInvitationSchema}
      isPending={isMutateCreateViewLinkInvitationPending}
      defaultRole="commenter"
      invalidEmailList={currentEmails}
      isError={isError || isMutateCreateViewLinkInvitationError}
      onEmailAdded={() => {
        tracker.trackShareModalEmailAdded({
          projectId: projectId,
          sharedResource: "project",
        });
      }}
      onInvalidEmailAdded={() => {
        tracker.trackShareModalInvalidEmailAdded({
          projectId: projectId,
          sharedResource: "project",
        });
      }}
      onRoleChanged={(newValue: string, oldValue: string) => {
        tracker.trackShareModalInviteRoleChanged({
          newValue: newValue,
          oldValue: oldValue,
          projectId: projectId,
          sharedResource: "project",
        });
      }}
      onEmailRemoved={() => {
        tracker.trackShareModalEmailRemoved({
          projectId: projectId,
          sharedResource: "project",
        });
      }}
      onInvitedPressed={(role: ProjectSharingBaseRole) => {
        tracker.trackShareModalInvitePressed({
          projectId: projectId,
          role: role,
          sharedResource: "project",
        });
      }}
    />
  );
};
